<template>
  <div class="ui-select" :class="hook ? `hook-${hook}` : ''" ref="root" v-if="list && list.length">
    <UiButton class="ui-select-trigger" hook @click.stop="toggle">
      <div class="label-box">
        <span class="label-shadow">{{ selectedName }}</span>
        <span class="label-text">{{ selectedName }}</span>
      </div>
      <i class="arrow" :class="{ open }"></i>
    </UiButton>
    <div class="ui-select-panel" v-show="open">
      <ul :class="hook ? `hook-${hook}` : ''">
        <li
          v-for="item in list"
          :key="item.id"
          :class="{ active: item.id === selectedId }"
          @click="handleSelect(item.id)"
        >
          <div class="label-box">
            <span class="label-shadow">{{ item.name }}</span>
            <span class="label-text">{{ item.name }}</span>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import UiButton from '@/components/ui-button'

defineOptions({ name: 'UiSelect' })

const props = defineProps({
  list: { type: Array, default: () => [] },
  // 受控: 当前选中项 id; 未匹配时兜底显示 list[0].name
  selectedId: { type: [String, Number], default: '' },
  // 列表样式钩子: '' 走 vh (设计稿基准), 'px' 走绝对像素 (弹窗内用)
  hook: { type: String, default: '' }
})

const emit = defineEmits(['switch'])

const open = ref(false)
const root = ref(null)

const selectedItem = computed(() =>
  props.list.find((x) => x.id === props.selectedId) || props.list[0]
)
const selectedName = computed(() => selectedItem.value?.name || '')

function toggle() {
  open.value = !open.value
}
function close() {
  open.value = false
}
function handleSelect(id) {
  emit('switch', id)
  close()
}

// 点击组件外部时关闭
function onDocClick(e) {
  if (root.value && !root.value.contains(e.target)) close()
}
onMounted(() => document.addEventListener('click', onDocClick))
onBeforeUnmount(() => document.removeEventListener('click', onDocClick))
</script>

<style lang="scss" scoped>
.ui-select {
  position: relative;
  width: 100%;
  height: 100%;
}

.ui-select-trigger {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  @include r(font-size, 20);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  :deep(.inner-box) {
    background: url(https://website.xdcdn.net/poster/58881/26match/common/PLuEfwdf.png) center/100% 100% no-repeat;

    .label-shadow {
      color: rgba(#000, 40%);
    }
  }

  .arrow {
    display: inline-block;
    position: absolute;
    @include r(width, 15);
    @include r(height, 13);
    @include r(right, 6);
    margin: auto 0;
    background: url(https://website.xdcdn.net/poster/58881/26match/common/PLvUpzej.png) center/contain no-repeat;
    transform-origin: center;
    transition: transform 200ms ease;
    &.open {
      transform: rotate(180deg);
    }
  }
}

// hook="px" 走绝对像素, 覆盖响应式默认 (弹窗内用, 避免和 ui-modal 的 scale 双重缩放)
.ui-select.hook-px {
  .ui-select-trigger {
    font-size: 20px;
    .arrow {
      width: 15px;
      height: 13px;
      right: 6px;
    }
  }
  // panel 落在 trigger 下方留 8% 间隙, 避免 vh 双重缩放后 top 变小导致重叠
  .ui-select-panel {
    top: 108%;
  }
}

.ui-select-panel {
  position: absolute;
  @include r(top, 42);
  width: 100%;
  right: 0;
  z-index: 20;
  background: url(https://website.xdcdn.net/poster/58881/26match/common/PLvhAzq9.png) center/100% 100% no-repeat;

  ul {
    box-sizing: border-box;
    width: 100%;
    @include r(border-radius, 10);
    @include r(padding, 8, 15);
    li {
      @include r(height, 37);
      @include r(line-height, 37);
      @include r(font-size, 20);
      @include r(border-bottom-width, 2);
      border-bottom-style: solid;
      border-bottom-color: #6A0002;
      text-align: center;
      white-space: nowrap;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      &:last-child {
        border: none;
      }
      .label-box .label-shadow {
        color: rgba(#000, 40%);
      }
      &.active .label-box .label-text {
        color: #FEE000 !important;
      }
    }

    // hook="px" 走绝对像素, 覆盖响应式默认 (弹窗内用)
    &.hook-px {
      border-radius: 10px;
      padding: 8px 15px;
      li {
        height: 37px;
        line-height: 37px;
        font-size: 20px;
        border-bottom-width: 2px;
      }
    }
  }
}
</style>
